import { XYLocation } from "@nens/deck-gl";

import { getV3Api } from "../apiConfiguration";
import { AppState, Thunk } from "../App";
import { forceValidOrNull } from "../utils/isValidData";

import { BACK_TO_START_SCREEN, BackToStartScreen } from "./appState";
import { Websocket, closeWebsocket, openWaterlevelProfileWebsocket } from "./websockets";

export type Line = XYLocation[];
export type MaybeLine = null | Line;
export type ArrayMaybeNumber = (number | null)[];
export type LineDataStatus = "loaded" | "loading" | null;

interface MapProfileLine {
  finished: boolean;
  line: MaybeLine;
  lineDataStatus: LineDataStatus;
  interpolatedDem: ArrayMaybeNumber;
  interpolatedGroundwater: ArrayMaybeNumber;
  interpolatedSurfacewater: ArrayMaybeNumber;
}
const INITIAL_STATE: MapProfileLine = {
  finished: false,
  line: null,
  // example values
  // interpolatedDem: [
  //   10.0,10.0,10.0,11.0,12.8,13.0,15.0,15.5,13.0,15.0,15.5,13.0,15.0,15.5,7.8,7.4,10.0,11.0,12.0,13.0,
  //   10.0,10.0,10.0,11.0,12.8,13.0,15.0,15.5,13.0,15.0,15.5,13.0,15.0,15.5,7.8,7.4,10.0,11.0,12.0,13.0,
  //   10.0,10.0,10.0,11.0,12.8,13.0,15.0,15.5,13.0,15.0,15.5,13.0,15.0,15.5,7.8,7.4,10.0,11.0,12.0,13.0
  // ],
  // interpolatedGroundwater: [
  //   8.0,8.0,8.0,7.0,9.8,9.0,9.0,9.5,9.0,9.0,10.5,9.0,9.0,9.5,5.8,5.4,6.0,7.0,8.0,9.0,
  //   8.0,8.0,8.0,7.0,9.8,9.0,9.0,9.5,9.0,9.0,10.5,9.0,9.0,0,-5,-4,6.0,7.0,8.0,9.0,
  //   8.0,8.0,8.0,7.0,9.8,9.0,9.0,9.5,9.0,9.0,10.5,9.0,9.0,9.5,5.8,5.4,6.0,7.0,8.0,9.0,
  // ],
  // interpolatedSurfacewater:[
  //   10.5,10.5,10.5,11.0,12.8,13.0,15.0,15.5,13.0,15.0,15.5,13.0,15.0,15.5,9.8,9.4,10.0,11.0,12.0,13.0,
  //   10.5,10.5,10.5,11.0,12.8,13.0,15.0,15.5,13.0,15.0,15.5,13.0,15.0,15.5,9.8,9.4,10.0,11.0,12.0,13.0,
  //   10.5,10.5,10.5,11.0,12.8,13.0,15.0,15.5,13.0,15.0,15.5,13.0,15.0,15.5,9.8,9.4,10.0,11.0,12.0,13.0,
  // ],
  lineDataStatus: null,
  interpolatedDem: [],
  interpolatedGroundwater: [], // currently not used
  interpolatedSurfacewater: [],
};

// Action types
const SET_FINISHED = "mapProfileLine/SET_FINISHED";
const SET_LINE = "mapProfileLine/SET_LINE";
const SET_LINE_DATA_STATUS = "mapProfileLine/SET_LINE_DATA_STATUS";
const REMOVE = "mapProfileLine/REMOVE";
const SETINTERPOLATEDDEM = "mapProfileLine/SETINTERPOLATEDDEM";
const SETINTERPOLATEDSURFACEWATER = "mapProfileLine/SETINTERPOLATEDSURFACEWATER";
const SETINTERPOLATEDGROUNDWATER = "mapProfileLine/SETINTERPOLATEDGROUNDWATER";
export const getInterpolatedDem = (state: AppState) => state.mapProfileLine.interpolatedDem;
export const getLineDataStatus = (state: AppState) => state.mapProfileLine.lineDataStatus;
export const getInterpolatedGroundwater = (state: AppState) =>
  state.mapProfileLine.interpolatedGroundwater;
export const getInterpolatedSurfacewater = (state: AppState) =>
  state.mapProfileLine.interpolatedSurfacewater;
export const getLine = (state: AppState) => state.mapProfileLine.line;
export const getFinished = (state: AppState) => state.mapProfileLine.finished;

export const setLineAndOpenWebsocket =
  (line: Line): Thunk =>
  (dispatch) => {
    dispatch(setFinished(true));
    dispatch(setLine(line));
    dispatch(setLineDataStatus("loading"));
    dispatch(openWaterlevelProfileWebsocket(line));
    dispatch(getElevationData());
  };

export const getElevationData = (): Thunk => async (dispatch, getState) => {
  const v3Api = getV3Api();
  const line = getState().mapProfileLine.line;
  if (line === null) {
    return;
  }

  const lineEWKT = `LINESTRING(${line.map((xy) => ` ${xy[0]} ${xy[1]} `)} )`;

  try {
    const simulation = getState().simulation.simulation;
    if (simulation === null) return;
    const profileSimulationId = simulation.id;
    if (profileSimulationId === undefined) return;
    const profiles = await v3Api.simulationsVisualisationsProfileList({
      simulationPk: profileSimulationId,
      layer: "dem",
    });
    const { url } = profiles;
    const response = await fetch(url.replace("{line}", lineEWKT).replace("{samples}", "61")); // 61 is equal to points for groundwater and surfacewater
    const values: number[] = await response.json();
    dispatch(setLineDataStatus("loaded"));
    dispatch(setInterpolatedDem(forceValidOrNull(values)));
  } catch (error) {
    console.error("Caught error wait for dem profile:", error);
    return;
  }
};
interface SetFinished {
  type: typeof SET_FINISHED;
  finished: boolean;
}
interface SetLine {
  type: typeof SET_LINE;
  line: Line;
}
interface SetLineDataStatus {
  type: typeof SET_LINE_DATA_STATUS;
  lineDataStatus: LineDataStatus;
}
interface Remove {
  type: typeof REMOVE;
}
interface SetInterpolatedDem {
  type: typeof SETINTERPOLATEDDEM;
  values: ArrayMaybeNumber;
}
interface SetInterpolatedSurfacewater {
  type: typeof SETINTERPOLATEDSURFACEWATER;
  values: ArrayMaybeNumber;
}
interface SetInterpolatedGroundwater {
  type: typeof SETINTERPOLATEDGROUNDWATER;
  values: ArrayMaybeNumber;
}
type MapProfileLineAction =
  | SetFinished
  | SetLine
  | SetLineDataStatus
  | Remove
  | SetInterpolatedDem
  | SetInterpolatedSurfacewater
  | SetInterpolatedGroundwater
  | BackToStartScreen;

export const setFinished = (finished: boolean): MapProfileLineAction => {
  return {
    type: SET_FINISHED,
    finished,
  };
};

export const setLine = (line: Line): MapProfileLineAction => {
  return {
    type: SET_LINE,
    line,
  };
};

export const setLineDataStatus = (lineDataStatus: LineDataStatus): MapProfileLineAction => {
  return {
    type: SET_LINE_DATA_STATUS,
    lineDataStatus,
  };
};

export const remove = (): Thunk => (dispatch) => {
  dispatch({
    type: REMOVE,
  });
  // Close current active WaterlevelProfileChart websocket
  dispatch(setLineDataStatus(null));
  dispatch(closeWebsocket(Websocket.WaterlevelProfileChart));
};
export const setInterpolatedDem = (values: ArrayMaybeNumber): SetInterpolatedDem => {
  return {
    type: SETINTERPOLATEDDEM,
    values: values,
  };
};
export const setInterpolatedSurfacewater = (
  values: ArrayMaybeNumber
): SetInterpolatedSurfacewater => {
  return {
    type: SETINTERPOLATEDSURFACEWATER,
    values: values,
  };
};
export const setInterpolatedGroundwater = (
  values: ArrayMaybeNumber
): SetInterpolatedGroundwater => {
  return {
    type: SETINTERPOLATEDGROUNDWATER,
    values: values,
  };
};

export default function reducer(
  state = INITIAL_STATE,
  action: MapProfileLineAction
): MapProfileLine {
  switch (action.type) {
    case BACK_TO_START_SCREEN:
      return INITIAL_STATE;
    case SET_FINISHED: {
      const { finished } = action;
      return {
        ...state,
        finished,
      };
    }
    case SET_LINE: {
      const { line } = action;
      return {
        ...state,
        line,
      };
    }

    case SET_LINE_DATA_STATUS: {
      const { lineDataStatus } = action;
      return {
        ...state,
        lineDataStatus,
      };
    }

    case REMOVE: {
      return {
        ...state,
        line: null,
        interpolatedDem: [],
        interpolatedSurfacewater: [],
        interpolatedGroundwater: [],
      };
    }
    case SETINTERPOLATEDDEM: {
      return {
        ...state,
        interpolatedDem: action.values,
      };
    }
    case SETINTERPOLATEDSURFACEWATER: {
      return {
        ...state,
        interpolatedSurfacewater: action.values,
      };
    }
    case SETINTERPOLATEDGROUNDWATER: {
      return {
        ...state,
        interpolatedGroundwater: action.values,
      };
    }
    default:
      return state;
  }
}
